import { Link } from "react-router-dom";
import { MapPin, Wallet, Trophy } from "lucide-react";
import { feeRange } from "@/lib/api";

// Deterministic colour from name — picks an "accent" per school so the icon cards
// don't look like clones. All colours harmonise with the brand palette.
const ACCENT_PRESETS = [
  { roof: "#C85A32", wall: "#F7E7DA", trim: "#1E3A2B", flag: "#DAA520", sky: "#FBE8D4" },
  { roof: "#1E3A2B", wall: "#DDE9DF", trim: "#C85A32", flag: "#DAA520", sky: "#E6F0E8" },
  { roof: "#DAA520", wall: "#FBF2DA", trim: "#1E3A2B", flag: "#C85A32", sky: "#FFF2CF" },
  { roof: "#2A4E3B", wall: "#E3EDE5", trim: "#DAA520", flag: "#C85A32", sky: "#D8E8DD" },
  { roof: "#A84624", wall: "#F7DECF", trim: "#1E3A2B", flag: "#DAA520", sky: "#FADCC6" },
  { roof: "#1C1917", wall: "#E6E2DA", trim: "#DAA520", flag: "#C85A32", sky: "#EDE7DB" },
];

function pickPreset(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return ACCENT_PRESETS[h % ACCENT_PRESETS.length];
}

function SchoolBuildingIcon({ accent }) {
  const { roof, wall, trim, flag, sky } = accent;
  return (
    <svg
      viewBox="0 0 240 180"
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={sky} stopOpacity="1" />
          <stop offset="100%" stopColor="#FDFBF7" stopOpacity="1" />
        </linearGradient>
        <linearGradient id="wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={wall} stopOpacity="1" />
          <stop offset="100%" stopColor={wall} stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id="roof" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={roof} stopOpacity="1" />
          <stop offset="100%" stopColor={roof} stopOpacity="0.78" />
        </linearGradient>
      </defs>

      {/* Sky background */}
      <rect width="240" height="180" fill="url(#sky)" />

      {/* Decorative dots */}
      <g opacity="0.18" fill={trim}>
        <circle cx="30" cy="30" r="1.6" />
        <circle cx="60" cy="18" r="1.2" />
        <circle cx="210" cy="25" r="1.6" />
        <circle cx="188" cy="40" r="1.1" />
      </g>

      {/* Ground shadow */}
      <ellipse cx="120" cy="162" rx="86" ry="6" fill={trim} opacity="0.12" />

      {/* Side wing (depth) */}
      <path d="M44 118 L74 96 L74 150 L44 150 Z" fill={wall} opacity="0.75" />
      <path d="M44 118 L74 96 L92 96 L62 118 Z" fill={roof} opacity="0.78" />

      {/* Main body */}
      <rect x="62" y="80" width="120" height="72" fill="url(#wall)" />
      {/* Main roof */}
      <polygon
        points="54,80 190,80 174,52 70,52"
        fill="url(#roof)"
      />
      {/* Roof trim line */}
      <rect x="54" y="80" width="136" height="4" fill={trim} opacity="0.75" />

      {/* Clock / seal in centre */}
      <circle cx="122" cy="68" r="7" fill={flag} />
      <circle cx="122" cy="68" r="4.5" fill={trim} opacity="0.9" />

      {/* Columns */}
      <rect x="82" y="100" width="7" height="52" fill={trim} opacity="0.78" />
      <rect x="110" y="100" width="7" height="52" fill={trim} opacity="0.78" />
      <rect x="138" y="100" width="7" height="52" fill={trim} opacity="0.78" />
      <rect x="166" y="100" width="7" height="52" fill={trim} opacity="0.78" />

      {/* Door */}
      <path
        d="M114 128 L114 152 L130 152 L130 128 A8 8 0 0 0 114 128 Z"
        fill={trim}
        opacity="0.92"
      />
      <circle cx="127" cy="140" r="1.2" fill={flag} />

      {/* Windows row (top) */}
      <g fill={trim} opacity="0.78">
        <rect x="93" y="92" width="12" height="8" rx="1.5" />
        <rect x="121" y="92" width="12" height="8" rx="1.5" />
        <rect x="149" y="92" width="12" height="8" rx="1.5" />
      </g>
      {/* Windows row (bottom) */}
      <g fill={trim} opacity="0.78">
        <rect x="93" y="120" width="12" height="8" rx="1.5" />
        <rect x="149" y="120" width="12" height="8" rx="1.5" />
      </g>

      {/* Flagpole + flag */}
      <line x1="122" y1="52" x2="122" y2="30" stroke={trim} strokeWidth="1.5" strokeLinecap="round" />
      <polygon points="122,30 138,34 122,40" fill={flag} />

      {/* Hedges */}
      <circle cx="56" cy="150" r="7" fill={trim} opacity="0.6" />
      <circle cx="68" cy="150" r="6" fill={trim} opacity="0.55" />
      <circle cx="188" cy="150" r="7" fill={trim} opacity="0.6" />
      <circle cx="176" cy="150" r="6" fill={trim} opacity="0.55" />
    </svg>
  );
}

export default function SchoolCard({ school }) {
  const testIdSafe = (school.slug || school.id || "school").replace(
    /[^a-z0-9-]/gi,
    "-"
  );
  const accent = pickPreset(school.name || "");
  return (
    <Link
      to={`/schools/${school.slug || school.id}`}
      data-testid={`school-card-${testIdSafe}`}
      className="group bg-white rounded-2xl border border-stone-200 overflow-hidden hover:border-[#C85A32]/40 hover:shadow-[0_12px_32px_-12px_rgba(200,90,50,0.25)] transition-all"
    >
      <div
        className="relative aspect-[16/10] overflow-hidden"
        style={{ background: accent.sky }}
      >
        {school.rank ? (
          <span
            className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-[#DAA520] text-stone-900 z-10"
            aria-label={`Ranked number ${school.rank}`}
            data-testid={`school-rank-${testIdSafe}`}
          >
            <Trophy className="w-3 h-3" /> #{school.rank}
          </span>
        ) : null}
        <span
          className={`absolute top-3 left-3 inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full z-10 ${
            school.type === "Public"
              ? "bg-indigo-100 text-indigo-800"
              : "bg-amber-100 text-amber-800"
          }`}
        >
          {school.type || "Private"}
        </span>
        <div className="absolute inset-0 flex items-center justify-center transform group-hover:scale-[1.03] transition-transform duration-500">
          <SchoolBuildingIcon accent={accent} />
        </div>
        {/* Name plate */}
        <div
          className="absolute bottom-0 left-0 right-0 px-4 py-2 backdrop-blur-sm border-t"
          style={{
            background: "rgba(253,251,247,0.9)",
            borderColor: `${accent.trim}22`,
          }}
        >
          <div
            className="font-display font-bold text-sm sm:text-base text-stone-900 leading-tight text-center truncate"
            title={school.name}
          >
            {school.name}
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center text-sm text-stone-600 gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-[#C85A32] shrink-0" />
          <span className="truncate">{school.district}</span>
        </div>
        <div className="mt-1.5 flex items-center text-sm text-stone-600 gap-1.5">
          <Wallet className="w-3.5 h-3.5 text-[#1E3A2B] shrink-0" />
          <span className="truncate">{feeRange(school)}/yr</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {(school.levels || []).slice(0, 4).map((lvl) => (
            <span
              key={lvl}
              className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-stone-100 text-stone-700"
            >
              {lvl}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}
